/**
 * Customer Exposure Calculation Model
 * 
 * Calculates EBITDA at risk from customer concentration using:
 * 1) EBITDA Margin = Base EBITDA / Revenue
 * 2) Largest Customer Exposure ($) = Revenue × Largest Customer % × EBITDA Margin
 * 3) Exposure % of Base EBITDA = Exposure $ / Base EBITDA
 * 
 * Uses Base EBITDA from the EBITDA Definition Unifier (QoE → Seller → Reported)
 */

import { selectBaseEbitda, isSourcedEbitda } from './ebitda';
import { formatMoney } from '../format/formatMoney';

/**
 * @typedef {Object} EvidenceRef
 * @property {string} docName
 * @property {number} [page]
 * @property {string} snippet
 */

/**
 * @typedef {Object} CustomerExposureResult
 * @property {boolean} canCompute
 * @property {string[]} [missingInputs]
 * @property {number|null} largestCustomerPct
 * @property {number|null} ebitdaMargin
 * @property {number|null} exposureDollar
 * @property {number|null} exposurePctOfEbitda
 * @property {"Moderate"|"Elevated"|"Severe"|null} severity
 * @property {Object} clauseEscalations
 * @property {boolean} clauseEscalations.noMsa
 * @property {boolean} clauseEscalations.noAssignment
 * @property {Object} evidence
 * @property {EvidenceRef[]} evidence.revenue
 * @property {EvidenceRef[]} evidence.baseEbitda
 * @property {EvidenceRef[]} evidence.largestCustomerPct
 * @property {Object} calculated
 * @property {Object} calculated.ebitdaMargin
 * @property {string} calculated.ebitdaMargin.formula
 * @property {string} calculated.ebitdaMargin.rendered
 */

/**
 * Severity thresholds for customer exposure
 */
const SEVERITY_THRESHOLDS = {
  SEVERE: 0.20,    // > 20% of Base EBITDA
  ELEVATED: 0.10   // 10% to 20%
};

/**
 * Compute customer exposure calculation
 * 
 * @param {Object} params
 * @param {Object} params.financials - Financial data with EBITDA variants and revenue
 * @param {number|null} params.largestCustomerPct - Largest customer % of revenue (decimal, e.g., 0.21)
 * @param {EvidenceRef[]} params.largestCustomerEvidence - Evidence for largest customer %
 * @param {boolean|null} params.hasMsa - Whether customer has MSA
 * @param {boolean|null} params.hasAssignmentClause - Whether contract has assignment clause
 * @returns {CustomerExposureResult}
 */
export function computeCustomerExposure({
  financials,
  largestCustomerPct,
  largestCustomerEvidence = [],
  hasMsa = null,
  hasAssignmentClause = null
}) {
  // Validate required inputs
  const missingInputs = [];

  // Get Base EBITDA using the unifier
  const baseEbitdaResult = selectBaseEbitda(financials);
  const baseEbitda = baseEbitdaResult.value;
  const baseEbitdaEvidence = baseEbitdaResult.sourceData?.evidence || [];

  if (baseEbitda === null || baseEbitda === undefined) {
    missingInputs.push('Base EBITDA');
  }

  // Get Revenue (must be sourced, period aligned with Base EBITDA)
  const revenue = financials?.revenue?.value ?? null;
  const revenueEvidence = financials?.revenue?.evidence || [];

  if (revenue === null || revenue === undefined) {
    missingInputs.push('Revenue');
  }

  // Validate largest customer %
  if (largestCustomerPct === null || largestCustomerPct === undefined) {
    missingInputs.push('Largest customer % of revenue');
  }

  // If any required input is missing, return not available
  if (missingInputs.length > 0) {
    return {
      canCompute: false,
      missingInputs,
      largestCustomerPct: largestCustomerPct ?? null,
      ebitdaMargin: null,
      exposureDollar: null,
      exposurePctOfEbitda: null,
      severity: null,
      clauseEscalations: {
        noMsa: hasMsa === false,
        noAssignment: hasAssignmentClause === false
      },
      evidence: {
        revenue: revenueEvidence,
        baseEbitda: baseEbitdaEvidence,
        largestCustomerPct: largestCustomerEvidence
      },
      calculated: {
        ebitdaMargin: {
          formula: 'Base EBITDA / Revenue',
          rendered: 'Not available'
        }
      }
    };
  }

  // Normalize largestCustomerPct to decimal (ensure it's not stored as 21 instead of 0.21)
  const normalizedCustomerPct = largestCustomerPct > 1 ? largestCustomerPct / 100 : largestCustomerPct;

  // 1) Calculate EBITDA Margin = Base EBITDA / Revenue
  const ebitdaMargin = revenue > 0 ? baseEbitda / revenue : 0;

  // 2) Calculate Largest Customer Exposure ($) = Revenue × Largest Customer % × EBITDA Margin
  // This simplifies to: Base EBITDA × Largest Customer %
  // But we calculate explicitly for clarity
  const revenueAtRisk = revenue * normalizedCustomerPct;
  const exposureDollar = revenueAtRisk * ebitdaMargin;

  // 3) Calculate Exposure % of Base EBITDA = Exposure $ / Base EBITDA
  const exposurePctOfEbitda = baseEbitda > 0 ? exposureDollar / baseEbitda : 0;

  // Determine base severity
  let severity;
  if (exposurePctOfEbitda > SEVERITY_THRESHOLDS.SEVERE) {
    severity = 'Severe';
  } else if (exposurePctOfEbitda >= SEVERITY_THRESHOLDS.ELEVATED) {
    severity = 'Elevated';
  } else {
    severity = 'Moderate';
  }

  // Apply clause escalation modifier
  // If "no MSA" OR "assignment clause absent" → increase severity by one level
  const clauseEscalations = {
    noMsa: hasMsa === false,
    noAssignment: hasAssignmentClause === false
  };

  const shouldEscalate = clauseEscalations.noMsa || clauseEscalations.noAssignment;
  
  if (shouldEscalate && severity !== 'Severe') {
    severity = severity === 'Moderate' ? 'Elevated' : 'Severe';
  }

  return {
    canCompute: true,
    missingInputs: [],
    largestCustomerPct: normalizedCustomerPct,
    ebitdaMargin,
    exposureDollar,
    exposurePctOfEbitda,
    severity,
    clauseEscalations,
    evidence: {
      revenue: revenueEvidence,
      baseEbitda: baseEbitdaEvidence,
      largestCustomerPct: largestCustomerEvidence
    },
    calculated: {
      ebitdaMargin: {
        formula: 'Base EBITDA / Revenue',
        rendered: `${formatMoney(baseEbitda)} / ${formatMoney(revenue)} = ${(ebitdaMargin * 100).toFixed(1)}%`
      }
    }
  };
}

/**
 * Get severity color and styling
 * @param {"Moderate"|"Elevated"|"Severe"|null} severity
 * @returns {{ bg: string, color: string, border: string }}
 */
export function getSeverityStyle(severity) {
  switch (severity) {
    case 'Severe':
      return {
        bg: '#FEE2E2',
        color: '#991B1B',
        border: '#FCA5A5'
      };
    case 'Elevated':
      return {
        bg: '#FEF3C7',
        color: '#92400E',
        border: '#FCD34D'
      };
    case 'Moderate':
      return {
        bg: '#D1FAE5',
        color: '#065F46',
        border: '#6EE7B7'
      };
    default:
      return {
        bg: '#F3F4F6',
        color: '#6B7280',
        border: '#D1D5DB'
      };
  }
}

/**
 * Format exposure for display
 * @param {CustomerExposureResult} exposure
 * @returns {{
 *   largestCustomerPct: string,
 *   ebitdaMargin: string,
 *   exposureDollar: string,
 *   exposurePctOfEbitda: string,
 *   severity: string
 * }}
 */
export function formatExposureDisplay(exposure) {
  if (!exposure || !exposure.canCompute) {
    return {
      largestCustomerPct: '—',
      ebitdaMargin: '—',
      exposureDollar: 'Not available',
      exposurePctOfEbitda: '—',
      severity: '—'
    };
  }

  return {
    largestCustomerPct: `${(exposure.largestCustomerPct * 100).toFixed(1)}%`,
    ebitdaMargin: `${(exposure.ebitdaMargin * 100).toFixed(1)}%`,
    exposureDollar: formatMoney(exposure.exposureDollar),
    exposurePctOfEbitda: `${(exposure.exposurePctOfEbitda * 100).toFixed(1)}%`,
    severity: exposure.severity
  };
}

/**
 * Get missing inputs display list
 * @param {string[]} missingInputs
 * @returns {string[]}
 */
export function getMissingInputsDisplay(missingInputs) {
  const displayMap = {
    'Base EBITDA': 'Base EBITDA (from QoE/Seller/Reported)',
    'Revenue': 'Revenue (period aligned with EBITDA)',
    'Largest customer % of revenue': 'Largest customer % of revenue'
  };

  return missingInputs.map(input => displayMap[input] || input);
}

export default {
  computeCustomerExposure,
  getSeverityStyle,
  formatExposureDisplay,
  getMissingInputsDisplay,
  SEVERITY_THRESHOLDS
};
